import React, { useState, useEffect } from 'react';
import { ApolloError } from '@apollo/client';
import { Box, Button, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    color: '#856404',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '0.875rem',
    overflow: 'auto',
    maxHeight: '200px',
    color: '#212529',
  },
  debugToggle: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#e9ecef',
    border: '1px solid #ced4da',
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    color: '#6c757d',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#dee2e6',
    },
  },
}));

interface GraphQLErrorHandlerProps {
  error: ApolloError | Error;
  /** Custom title for the error message */
  title?: string;
  /** Custom message to display */
  message?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Show reload button */
  showReload?: boolean;
  /** Show back button */
  showBack?: boolean;
  /** Callback for back action */
  onBack?: () => void;
  /** Show error details in development (deprecated - use Shift+D instead) */
  showDetails?: boolean;
}

/**
 * Reusable component for handling and displaying GraphQL errors with user-friendly messages.
 * Automatically detects error types and provides appropriate actions.
 */
export const GraphQLErrorHandler: React.FC<GraphQLErrorHandlerProps> = ({
  error,
  title,
  message,
  showRetry = true,
  onRetry,
  showReload = true,
  showBack = false,
  onBack,
}) => {
  const classes = useStyles();
  const [showDebugDetails, setShowDebugDetails] = useState(false);

  // Listen for Shift+D keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key === 'D') {
        setShowDebugDetails(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Extract user-friendly error message
  const getUserMessage = (): string => {
    if (message) return message;

    const errorMessage = error.message || '';

    // Check for specific error patterns
    if (errorMessage.includes('not found') || errorMessage.includes('does not exist')) {
      return 'The requested data could not be found. It may have been deleted or you may not have permission to access it.';
    }

    if (errorMessage.includes('schema error') || errorMessage.includes('Data schema error')) {
      return 'There is a data structure issue. Please contact support.';
    }

    if (errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
      return 'The request took too long to complete. Please try again.';
    }

    if (errorMessage.includes('permission') || errorMessage.includes('Unauthorized')) {
      return 'You do not have permission to access this data.';
    }

    if (errorMessage.includes('Database error')) {
      return 'A database error occurred. Please try again or contact support if the problem persists.';
    }

    // Default message
    return 'An unexpected error occurred while loading the data. Please try again or contact support if the problem persists.';
  };

  const getErrorTitle = (): string => {
    if (title) return title;

    const errorMessage = error.message || '';

    if (errorMessage.includes('not found')) {
      return 'Data Not Found';
    }

    if (errorMessage.includes('Network error')) {
      return 'Connection Error';
    }

    if (errorMessage.includes('permission') || errorMessage.includes('Unauthorized')) {
      return 'Access Denied';
    }

    return 'Error Loading Data';
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  // Extract GraphQL errors if available
  const graphQLErrors = (error as ApolloError).graphQLErrors || [];
  const networkError = (error as ApolloError).networkError;

  return (
    <Box className={classes.container}>
      <Alert severity="error" className={classes.alert}>
        <AlertTitle>{getErrorTitle()}</AlertTitle>
        <Typography variant="body2">{getUserMessage()}</Typography>

        <Box className={classes.buttonGroup}>
          {showRetry && (
            <Button variant="contained" color="primary" onClick={handleRetry} size="small">
              {onRetry ? 'Retry' : 'Reload Page'}
            </Button>
          )}
          {showReload && !showRetry && (
            <Button variant="outlined" onClick={() => window.location.reload()} size="small">
              Reload Page
            </Button>
          )}
          {showBack && (
            <Button variant="outlined" onClick={handleBack} size="small">
              Go Back
            </Button>
          )}
        </Box>
      </Alert>

      {showDebugDetails && (
        <Box>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, color: '#495057' }}>
            🔍 Debug Information
          </Typography>

          {graphQLErrors.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" display="block" gutterBottom>
                GraphQL Errors:
              </Typography>
              <Box className={classes.errorDetails}>
                {graphQLErrors.map((err, index) => (
                  <Box key={index} mb={1}>
                    <Typography variant="caption" component="div">
                      <strong>Error {index + 1}:</strong> {err.message}
                    </Typography>
                    {err.path && (
                      <Typography variant="caption" component="div" color="textSecondary">
                        Path: {err.path.join(' → ')}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {networkError && (
            <Box mb={2}>
              <Typography variant="caption" display="block" gutterBottom>
                Network Error:
              </Typography>
              <Box className={classes.errorDetails}>
                <Typography variant="caption">{networkError.message}</Typography>
              </Box>
            </Box>
          )}

          {error.stack && (
            <Box>
              <Typography variant="caption" display="block" gutterBottom>
                Stack Trace:
              </Typography>
              <Box className={classes.errorDetails}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {error.stack}
                </pre>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GraphQLErrorHandler;
